import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import {
    IN_PAGE_SEARCH_PARAM_KEY,
    IN_PAGE_SEARCH_SORT_BY,
    IN_PAGE_SEARCH_ENABLED_SHOP,
} from '@modules/professional/config/constants';

export const useProfessionalTabSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Written on a copy: the object `useSearchParams` hands back is the live
    // one, so setting a key on it changed what the rest of the render read
    // before the navigation had happened.
    const onParamChange = useCallback(
        (paramName: string, paramValue = '') => {
            const next = new URLSearchParams(searchParams);
            if (paramValue) {
                next.set(paramName, paramValue);
            } else {
                next.delete(paramName);
            }
            setSearchParams(next, { state: { scroll: false } });
        },
        [searchParams, setSearchParams],
    );

    // Stable, so an effect that commits a debounced term can depend on it
    // without re-arming on every render of the field.
    const onSearchChange = useCallback(
        (searchKeyword = '') =>
            onParamChange(IN_PAGE_SEARCH_PARAM_KEY, searchKeyword),
        [onParamChange],
    );

    const onSortChange = useCallback(
        (sortBy = '') => onParamChange(IN_PAGE_SEARCH_SORT_BY, sortBy),
        [onParamChange],
    );

    const onShopChange = useCallback(
        (enabledShop = '') =>
            onParamChange(
                IN_PAGE_SEARCH_ENABLED_SHOP,
                enabledShop === 'yes' ? enabledShop : '',
            ),
        [onParamChange],
    );

    return {
        onSearchChange,
        onSortChange,
        onShopChange,
        defaultKeyword: searchParams.get(IN_PAGE_SEARCH_PARAM_KEY) || '',
        defaultSortBy: searchParams.get(IN_PAGE_SEARCH_SORT_BY) || 'popular',
        defaultShop: searchParams.get(IN_PAGE_SEARCH_ENABLED_SHOP) || '',
    };
};
